import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums.ts';
import { MediaType } from './Chat.enum.ts';
import { tAttachment, tLocationPlace, tOutboundAttachment } from './Chat.types';
import {
  isWhatsAppWebChannelReadOnly,
  tWhatsAppWebConnectionPhase,
} from './useWhatsAppWebConnection.hook.ts';

/**
 * Rebuilds a decrypted attachment, replacing only `url` and preserving every
 * other field via spread.
 *
 * Replaces the field-by-field object literals previously inlined in
 * `decryptAttachment` / `decryptAndParseAllMessages` (Chat.actions.ts). Those
 * literals enumerated the known `tAttachment` keys, so they silently dropped
 * any field not in the list — including the SHARE_POST link-preview fields
 * (`video_id`, `title`, `author_name`, `author_url`, `thumbnail_url`,
 * `embed_url`) added for INT-929. Spreading instead of enumerating means
 * future `tAttachment` fields propagate through decryption automatically.
 */
export function withDecryptedUrl(
  attachment: tAttachment,
  url: tAttachment['url'],
): tAttachment {
  return { ...attachment, url };
}

type tShouldBlockSendParams = {
  /** The channel the outbound message / forward is targeting. */
  channel: eChannelType;
  /** The live WhatsApp Web connection phase from `useWhatsAppWebConnection`. */
  whatsAppWebPhase: tWhatsAppWebConnectionPhase;
  /** The cached `IntegrationsProvider.hasWaWebAccount()` result. */
  hasWaWebAccount: boolean;
};

/**
 * Defense-in-depth pre-send guard (INT-941 / U3). Returns `true` when an
 * outbound message or forward must be blocked because its target channel is a
 * WhatsApp Web channel whose account no longer exists.
 *
 * Without this, a send to a deleted `WHATSAPP_WEB` channel reaches the dead
 * provider and fails silently with error 1003 (`UNKNOWN_ERROR_FROM_PROVIDER`)
 * and a generic red error bar. The caller checks this first and surfaces a
 * clear, explanatory toast instead.
 *
 * The block condition mirrors the U2 read-only composer exactly (via the
 * shared `isWhatsAppWebChannelReadOnly`) so the guard and the composer never
 * disagree: `absent` blocks; `pending` / `error` degrade to the cached
 * `hasWaWebAccount` hint rather than hard fail-closed, so a transient blip on
 * a healthy pure-WhatsApp-Web workspace is not over-blocked.
 *
 * Channel-generic by signature, but only `WHATSAPP_WEB` has a live check
 * wired today — every other channel passes through unblocked.
 */
export function shouldBlockSendToReadOnlyChannel(
  params: tShouldBlockSendParams,
): boolean {
  const { channel, whatsAppWebPhase, hasWaWebAccount } = params;

  if (channel !== eChannelType.WHATSAPP_WEB) {
    return false;
  }

  return isWhatsAppWebChannelReadOnly(whatsAppWebPhase, hasWaWebAccount);
}

type tShouldBlockSendWithoutWapiPlanParams = {
  /** The RESOLVED channel the forward goes out on (`selectedChat.integration`). */
  channel: eChannelType;
  /** `useWalletSurfaceAccess().isKnownWithoutWapiAccess`. */
  isKnownWithoutWapiAccess: boolean;
};

/**
 * RET-2339 (D6) — second layer behind the read-only composer. Returns `true`
 * when a send would go out on WhatsApp API from a workspace whose plan is
 * KNOWN not to include it. The backend does not refuse a send by plan (only
 * by balance), so this front guard is the only plan check.
 *
 * Fails open on purpose: an unresolved plan or exception never blocks, because
 * `isKnownWithoutWapiAccess` is `true` only with positive evidence.
 */
export function shouldBlockSendWithoutWapiPlan(
  params: tShouldBlockSendWithoutWapiPlanParams,
): boolean {
  return (
    params.isKnownWithoutWapiAccess &&
    params.channel === eChannelType.WHATSAPP_API
  );
}

/**
 * RET-2349 (KTD5) — thrown by a send producer that the plan guard refuses
 * while it runs inside the attachment loop. The producer throws it BEFORE its
 * own `try`, without a toast, so `sendAttachmentsSequentially` can treat it as
 * a refusal (stop, keep the tray, one `wapiPlanReadOnly.message` toast) and
 * not as a failed upload.
 *
 * Lives here, in the pure module, so non-React callers such as
 * `MessageBar.utils.ts` can recognise it without importing the hook.
 */
export class WapiPlanSendBlockedError extends Error {
  constructor() {
    super('Send refused: the workspace plan does not include WhatsApp API');
    this.name = 'WapiPlanSendBlockedError';
  }
}

export function isWapiPlanSendBlockedError(
  error: unknown,
): error is WapiPlanSendBlockedError {
  return error instanceof WapiPlanSendBlockedError;
}

/**
 * INT-1720 — the LOCATION attachment of an outbound location. A place the advisor
 * picked from the search adds its `name` and `address`, but only on WhatsApp API:
 * that is the only channel whose sender forwards them to the lead, and storing
 * them elsewhere would show the advisor a place name the lead never got. Blank
 * values are left out, so a bare pin keeps the shape it always had.
 */
export function buildLocationAttachment(
  latitude: number,
  longitude: number,
  channel: eChannelType,
  place?: tLocationPlace | null,
): tOutboundAttachment {
  const attachment: tOutboundAttachment = {
    media_type: MediaType.LOCATION,
    mimetype: 'location',
    latitude,
    longitude,
  };
  if (channel !== eChannelType.WHATSAPP_API || !place) {
    return attachment;
  }
  const name = place.name.trim();
  const address = place.address.trim();
  return {
    ...attachment,
    ...(name ? { name } : {}),
    ...(address ? { address } : {}),
  };
}
